import {extensionForContentTypeOrNull, isValidContentType} from "@cm/utils/content-types"
import {MATERIAL_THUMBNAIL_OUTPUT_LABELS, MATERIAL_THUMBNAIL_OUTPUT_TYPES} from "@cm/utils/material-thumbnail-constants"
import type {MaterialThumbnailOutputType} from "@cm/utils/material-thumbnail-constants"
import type {ResourceField, ResourceUpload} from "#material-outputs-publishing/targets/configura/client"

// Keyed by MaterialOutputType's string values, not the enum: that lives in the generated GraphQL SDK
// and this package deliberately does not depend on it. Every thumbnail size maps to `imageFile`, the
// Material Database's one such slot, spread from @cm/utils so a new size lands in a single place.
export const RESOURCE_FIELD_BY_MATERIAL_OUTPUT_TYPE: Record<string, ResourceField> = {
    ...Object.fromEntries(MATERIAL_THUMBNAIL_OUTPUT_TYPES.map((type) => [type, "imageFile" as const])),
    TileableImage: "repeatImageFile",
    ConfiguraExport: "zipFile",
}

/**
 * Every type that could ever be published by any organization — what a query must fetch *before* it
 * knows which thumbnail this organization publishes. Derived from the mapping rather than written
 * out again: a type absent here is never fetched, which coherence would then report as permanently
 * `missing` for an output that exists.
 */
export const PUBLISHABLE_MATERIAL_OUTPUT_TYPES: readonly string[] = Object.keys(RESOURCE_FIELD_BY_MATERIAL_OUTPUT_TYPE)

const isThumbnailOutputType = (type: string): type is MaterialThumbnailOutputType => type in MATERIAL_THUMBNAIL_OUTPUT_LABELS

/** The thumbnail sizes an organization may choose between — the Material Database's one `imageFile` slot. */
export function isThumbnailMaterialOutputType(type: string): boolean {
    return RESOURCE_FIELD_BY_MATERIAL_OUTPUT_TYPE[type] === "imageFile"
}

/**
 * How an output type is written for a person, for the messages publish state carries. Thumbnails are
 * named by size — which one an organization publishes is a setting, so "a thumbnail is missing" is
 * never a useful thing to tell someone.
 */
export function describeMaterialOutputType(type: string): string {
    if (isThumbnailOutputType(type)) {
        return `${MATERIAL_THUMBNAIL_OUTPUT_LABELS[type]} thumbnail`
    }
    switch (type) {
        case "TileableImage":
            return "tileable image"
        case "ConfiguraExport":
            return "Configura export"
        default:
            return type
    }
}

/** The one published output publishing can generate for itself, rather than waiting for a user to. */
export const CONFIGURA_EXPORT_OUTPUT_TYPE = "ConfiguraExport"

/** The types that must exist and share a revision before anything is pushed, for one organization. */
export function publishedMaterialOutputTypesFor(thumbnailMaterialOutputType: string): readonly string[] {
    return [thumbnailMaterialOutputType, "TileableImage", CONFIGURA_EXPORT_OUTPUT_TYPE]
}

/**
 * Which DataObject a slot's bytes come from. The image slots take a regenerated thumbnail variant;
 * `zipFile` takes the output's own result, because a ConfiguraExport is a zip of textures and
 * .mtlx documents and has no image variant to ask for.
 *
 * The variant's file type and resolution are recorded here rather than left at the call site, so
 * they read as a choice — they are still an unverified one, see open question (h) in the design doc.
 * The literals are the wire values of the API's DownloadFileType / DownloadResolution enums, which
 * this package cannot import: they live in the generated SDK.
 */
export type ResourceSource = {kind: "original"} | {kind: "thumbnailVariant"; fileType: "jpg"; resolution: "High"}

const THUMBNAIL_VARIANT: ResourceSource = {kind: "thumbnailVariant", fileType: "jpg", resolution: "High"}

export const RESOURCE_SOURCE_BY_FIELD: Record<ResourceField, ResourceSource> = {
    imageFile: THUMBNAIL_VARIANT,
    repeatImageFile: THUMBNAIL_VARIANT,
    zipFile: {kind: "original"},
}

export type PublishedDataObject = {
    originalFileName?: string | null
    mediaType?: string | null
}

export function resourceFieldForMaterialOutputType(type: string): ResourceField | undefined {
    return RESOURCE_FIELD_BY_MATERIAL_OUTPUT_TYPE[type]
}

/** What a slot's bytes are called on the wire, for either upload route. */
export type ResourceNaming = {
    /** Becomes the resource's `original-filename`. */
    fileName: string
    /** The bare extension the presigned mint wants as `fileType`. */
    fileType: string
    /** Becomes the resource's `mimetype`: the blob type on the multipart route, the `PUT`'s Content-Type on the presigned one. */
    contentType: string
}

const FALLBACK_CONTENT_TYPE = "application/octet-stream"
const FALLBACK_FILE_TYPE_BY_FIELD: Record<ResourceField, string> = {imageFile: "jpeg", repeatImageFile: "jpeg", zipFile: "zip"}

/**
 * The mint accepts `jpeg webp avif png svg gif zip` (their spec, 2026-10-07) and answers 400
 * "Unsupported file type" for anything else, including our own `jpg`. Spellings that differ from
 * ours are mapped here; anything else passes through and is refused by them, loudly.
 */
const MINT_FILE_TYPE_BY_EXTENSION: Record<string, string> = {jpg: "jpeg"}
const toMintFileType = (extension: string) => MINT_FILE_TYPE_BY_EXTENSION[extension] ?? extension

/**
 * The Material Database stores `fileName` as the resource's `original-filename` and the blob's type as its
 * `mimetype`, so both come from the output's result DataObject. `originalFileName` is free text and
 * can be blank, which would leave the resource nameless in the Material Database — hence the fallback.
 * The file type follows the media type, then the name's own extension, then what the slot is for.
 */
export function resourceNamingFor(field: ResourceField, dataObject: PublishedDataObject): ResourceNaming {
    const mediaType = isValidContentType(dataObject.mediaType) ? dataObject.mediaType : undefined
    const originalFileName = dataObject.originalFileName?.trim()
    const extension = mediaType ? extensionForContentTypeOrNull(mediaType) : null
    const fileName = originalFileName && originalFileName.length > 0 ? originalFileName : `${field}${extension ? `.${extension}` : ""}`
    const nameExtension = /\.([A-Za-z0-9]+)$/.exec(fileName)?.[1]?.toLowerCase()
    return {
        fileName,
        fileType: toMintFileType(extension ?? nameExtension ?? FALLBACK_FILE_TYPE_BY_FIELD[field]),
        contentType: mediaType ?? FALLBACK_CONTENT_TYPE,
    }
}

/** The multipart route's upload: the whole body as a Blob, named as `resourceNamingFor` names it. */
export function resourceUploadFor(field: ResourceField, dataObject: PublishedDataObject, loadBytes: () => Promise<ArrayBuffer | Uint8Array>): ResourceUpload {
    const {fileName, contentType} = resourceNamingFor(field, dataObject)
    const mediaType = isValidContentType(dataObject.mediaType) ? contentType : undefined
    return {
        body: async () => {
            const bytes = await loadBytes()
            // Cast because Uint8Array is generic over ArrayBufferLike, which includes SharedArrayBuffer
            // and so is not a BlobPart. Node's Buffer hits the same wall at every call site.
            return new Blob([bytes as BlobPart], mediaType ? {type: mediaType} : undefined)
        },
        fileName,
    }
}
